// ============================================================================
// G-007 — Report Export (Word .docx + PDF) for OSINT findings
// ============================================================================
// Per OSINT Flow Doc v2 § 4 Step 8 + Gap G-007:
//   - Word (.docx) + PDF (.pdf) format options
//   - Watermark: operator name + timestamp every page
//   - Classification stamp: "CONFIDENTIAL — INTERNAL USE ONLY" cover + footer
//   - ELEVATED data marking: "Contact data — sensitive" badge
//   - Findings consolidated by platform, sorted by confidence
//   - Audit log entry per generation (G-008)
//   - Daily quota: 20 reports/op/day default (G-008)
// ============================================================================

// Code split: docx + jspdf lazy-loaded saat user klik Export.
// Reduces initial bundle ~700KB → faster page load + faster Vercel deploy.
// import statements moved into async functions below.
import { supabase } from '../supabase';

const CLASSIFICATION = 'CONFIDENTIAL — INTERNAL USE ONLY';
const DAILY_QUOTA    = 20; // per operator per day

// ============================================================================
// Daily Quota Check (G-008)
// ============================================================================

/**
 * Check + increment daily quota counter via osint_audit query.
 * Returns { allowed: bool, used, limit, remaining }
 */
export async function checkReportQuota(operatorId, customLimit = DAILY_QUOTA) {
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const { count, error } = await supabase
    .from('osint_audit')
    .select('id', { count: 'exact', head: true })
    .eq('operator_id', operatorId)
    .eq('action', 'report_generated')
    .gte('created_at', startOfDay.toISOString());
  if (error) {
    console.warn('[reportExport] quota check fail:', error.message);
    return { allowed: true, used: 0, limit: customLimit, remaining: customLimit };
  }
  const used = count || 0;
  return {
    allowed:   used < customLimit,
    used,
    limit:     customLimit,
    remaining: Math.max(0, customLimit - used),
  };
}

// ============================================================================
// Audit Log Entry (G-008)
// ============================================================================

async function logReportGeneration(operatorId, nikSubject, format, findingsCount, elevatedCount) {
  try {
    await supabase.from('osint_audit').insert({
      action:      'report_generated',
      operator_id: operatorId,
      subject_id:  nikSubject,
      metadata: {
        format,
        findings_count: findingsCount,
        elevated_count: elevatedCount,
        timestamp:      new Date().toISOString(),
      },
    });
  } catch (err) {
    console.warn('[reportExport] audit log fail:', err.message);
  }
}

// ============================================================================
// Helpers
// ============================================================================

function fmtDate(iso, lang = 'id') {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(lang === 'id' ? 'id-ID' : 'en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function confidenceLabel(conf, lang) {
  if (conf >= 0.85) return lang === 'id' ? 'Match Tinggi' : 'High match';
  if (conf >= 0.65) return lang === 'id' ? 'Kemungkinan'   : 'Likely';
  if (conf >= 0.50) return lang === 'id' ? 'Mungkin'       : 'Possible';
  return                    lang === 'id' ? 'Lemah'         : 'Weak';
}

function groupByPlatform(findings) {
  const groups = {};
  for (const f of findings) {
    const key = f.platform || 'other';
    if (!groups[key]) groups[key] = [];
    groups[key].push(f);
  }
  for (const key of Object.keys(groups)) {
    groups[key].sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
  }
  return groups;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ============================================================================
// EXPORT 1 — Word (.docx)
// ============================================================================

export async function exportWordReport({ subject, findings, summary, operatorId, lang = 'id' }) {
  // Lazy-load docx library (~500KB) only saat operator klik Export Word
  const docx = await import('docx');
  const {
    Document, Packer, Paragraph, TextRun, HeadingLevel,
    AlignmentType, PageOrientation, Header, Footer, PageNumber,
  } = docx;

  const now           = new Date();
  const ts            = now.toISOString();
  const watermark     = `${operatorId} · ${fmtDate(ts, lang)}`;
  const elevatedCount = findings.filter(f => f.raw_data?.is_elevated).length;
  const groups        = groupByPlatform(findings);

  // Cover page
  const coverParas = [
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 1200, after: 240 },
      children: [
        new TextRun({ text: CLASSIFICATION, bold: true, color: 'C00000', size: 28 }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 800, after: 200 },
      children: [
        new TextRun({ text: lang === 'id' ? 'LAPORAN OSINT' : 'OSINT REPORT', bold: true, size: 56 }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 600 },
      children: [
        new TextRun({ text: 'HORUS V2 · Powered by KES', size: 20, color: '0084B8' }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 800, after: 100 },
      children: [
        new TextRun({ text: lang === 'id' ? 'Subjek:' : 'Subject:', bold: true, size: 24 }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: subject?.nama_lengkap || subject?.nik || '—', size: 28 })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 200 },
      children: [new TextRun({ text: `NIK: ${subject?.nik || '—'}`, size: 18 })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 600 },
      children: [
        new TextRun({ text: lang === 'id' ? 'Operator: ' : 'Operator: ', bold: true }),
        new TextRun({ text: operatorId }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({ text: lang === 'id' ? 'Dibuat: ' : 'Generated: ', bold: true }),
        new TextRun({ text: fmtDate(ts, lang) }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 200 },
      children: [
        new TextRun({ text: lang === 'id' ? 'Total Temuan: ' : 'Total Findings: ', bold: true }),
        new TextRun({ text: String(findings.length) }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({ text: lang === 'id' ? 'Data Sensitif: ' : 'Sensitive Fields: ', bold: true }),
        new TextRun({ text: String(elevatedCount), color: 'C00000' }),
      ],
    }),
    new Paragraph({ children: [new TextRun({ text: '', break: 1 })] }),
  ];

  // Findings sections per platform
  const findingsParas = [];
  for (const [platform, items] of Object.entries(groups)) {
    findingsParas.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 200 },
        children: [
          new TextRun({ text: platform.toUpperCase(), bold: true, color: '0084B8' }),
          new TextRun({ text: ` · ${items.length} ${lang === 'id' ? 'temuan' : 'findings'}`, size: 18, color: '666666' }),
        ],
      })
    );

    for (const f of items) {
      const r       = f.raw_data || {};
      const isElev  = r.is_elevated;
      const conf    = Math.round((f.confidence || 0) * 100);
      const label   = confidenceLabel(f.confidence || 0, lang);
      const hasPhone     = !!r.phone;
      const hasPostLoc   = !!r.post_tagged_location;

      findingsParas.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 200, after: 100 },
          children: [
            new TextRun({ text: r.publisher || r.authority || r.username || f.source_url || '—', bold: true }),
            new TextRun({ text: `   [${label} · ${conf}%]`, color: conf >= 65 ? '008844' : 'AA6600', size: 18 }),
            ...(isElev ? [new TextRun({ text: '   ⚠ ' + (lang === 'id' ? 'DATA SENSITIF' : 'SENSITIVE'), color: 'C00000', bold: true, size: 18 })] : []),
          ],
        })
      );

      if (r.headline) findingsParas.push(new Paragraph({ children: [new TextRun({ text: r.headline, italics: true })] }));
      if (r.snippet)  findingsParas.push(new Paragraph({ children: [new TextRun({ text: r.snippet, size: 18 })] }));
      if (r.text)     findingsParas.push(new Paragraph({ children: [new TextRun({ text: r.text.slice(0, 400), size: 18 })] }));
      if (r.key_data) findingsParas.push(new Paragraph({ children: [new TextRun({ text: r.key_data, size: 18 })] }));
      if (f.signal_explain) findingsParas.push(new Paragraph({ children: [new TextRun({ text: 'Match: ' + f.signal_explain, size: 16, color: '666666' })] }));

      // ELEVATED fields displayed dengan label sensitif
      if (hasPhone) {
        findingsParas.push(new Paragraph({
          children: [
            new TextRun({ text: '⚠ ', color: 'C00000', bold: true }),
            new TextRun({ text: lang === 'id' ? 'Telepon (Data Kontak Sensitif): ' : 'Phone (Sensitive): ', bold: true, color: 'C00000' }),
            new TextRun({ text: r.phone, color: 'C00000' }),
          ],
        }));
      }
      if (hasPostLoc) {
        findingsParas.push(new Paragraph({
          children: [
            new TextRun({ text: '⚠ ', color: 'CC8800', bold: true }),
            new TextRun({ text: lang === 'id' ? 'Lokasi Postingan (Sensitif): ' : 'Post Location (Sensitive): ', bold: true, color: 'CC8800' }),
            new TextRun({ text: r.post_tagged_location, color: 'CC8800' }),
          ],
        }));
      }
      if (f.source_url) {
        findingsParas.push(new Paragraph({ children: [new TextRun({ text: f.source_url, size: 14, color: '0066CC' })] }));
      }
      findingsParas.push(new Paragraph({ children: [new TextRun({ text: '─'.repeat(40), color: 'CCCCCC' })] }));
    }
  }

  // Footer disclaimer
  findingsParas.push(
    new Paragraph({
      spacing: { before: 600 },
      children: [
        new TextRun({ text: lang === 'id' ? 'Catatan Hukum: ' : 'Legal Notice: ', bold: true, color: '666666', size: 14 }),
        new TextRun({
          text: lang === 'id'
            ? 'Laporan ini berisi data publik yang dikumpulkan via OSINT untuk keperluan investigasi resmi pemerintah. Sesuai UU PDP, data sensitif (telepon, lokasi inferensi) memerlukan justifikasi tambahan untuk diakses ulang. Retensi default 24 bulan. Distribusi terbatas — internal use only.'
            : 'This report contains public data collected via OSINT for authorized government investigation. Per UU PDP, sensitive fields (phone, inferred location) require additional justification for re-access. Default retention 24 months. Limited distribution — internal use only.',
          color: '666666', size: 14, italics: true,
        }),
      ],
    })
  );

  // Document with header (watermark) + footer (classification + page number)
  const doc = new Document({
    creator:     'HORUS V2 — KES',
    title:       `OSINT Report — ${subject?.nama_lengkap || subject?.nik}`,
    description: `Generated by ${operatorId} at ${ts}`,
    sections: [{
      properties: { page: { size: { orientation: PageOrientation.PORTRAIT } } },
      headers: {
        default: new Header({
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new TextRun({ text: watermark, color: 'AAAAAA', size: 16 }),
              ],
            }),
          ],
        }),
      },
      footers: {
        default: new Footer({
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new TextRun({ text: CLASSIFICATION, bold: true, color: 'C00000', size: 14 }),
              ],
            }),
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new TextRun({ text: lang === 'id' ? 'Halaman ' : 'Page ', size: 12, color: '888888' }),
                new TextRun({ children: [PageNumber.CURRENT], size: 12, color: '888888' }),
                new TextRun({ text: ' / ', size: 12, color: '888888' }),
                new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 12, color: '888888' }),
                new TextRun({ text: '  ·  © 2026 KES (PT Kartika Eka Satria)', size: 12, color: '888888' }),
              ],
            }),
          ],
        }),
      },
      children: [...coverParas, ...findingsParas],
    }],
  });

  const blob = await Packer.toBlob(doc);
  const filename = `OSINT-Report-${subject?.nik || 'subject'}-${Date.now()}.docx`;
  downloadBlob(blob, filename);

  await logReportGeneration(operatorId, subject?.nik, 'docx', findings.length, elevatedCount);
  return { format: 'docx', filename, findings_count: findings.length, elevated_count: elevatedCount };
}

// ============================================================================
// EXPORT 2 — PDF
// ============================================================================

export async function exportPdfReport({ subject, findings, summary, operatorId, lang = 'id' }) {
  // Lazy-load jsPDF library (~150KB) only saat operator klik Export PDF
  const { default: jsPDF } = await import('jspdf');

  const now           = new Date();
  const ts            = now.toISOString();
  const watermark     = `${operatorId} · ${fmtDate(ts, lang)}`;
  const elevatedCount = findings.filter(f => f.raw_data?.is_elevated).length;
  const groups        = groupByPlatform(findings);

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Header/footer applier — call after each page break
  const stampPage = (pageNum, totalPages) => {
    // Watermark center-page faded
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.06 }));
    doc.setFontSize(64);
    doc.setTextColor(192, 0, 0);
    doc.text(CLASSIFICATION.split(' — ')[0], pageW / 2, pageH / 2, { angle: -45, align: 'center' });
    doc.restoreGraphicsState();

    // Top header — operator + timestamp watermark
    doc.setFontSize(8);
    doc.setTextColor(170, 170, 170);
    doc.text(watermark, pageW / 2, 20, { align: 'center' });

    // Bottom footer — classification + page number + copyright
    doc.setFontSize(9);
    doc.setTextColor(192, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text(CLASSIFICATION, pageW / 2, pageH - 28, { align: 'center' });
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(136, 136, 136);
    doc.text(
      `${lang === 'id' ? 'Halaman' : 'Page'} ${pageNum}${totalPages ? ' / ' + totalPages : ''}  ·  © 2026 KES (PT Kartika Eka Satria)`,
      pageW / 2, pageH - 16, { align: 'center' }
    );
  };

  // Cover page
  doc.setFontSize(11);
  doc.setTextColor(192, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.text(CLASSIFICATION, pageW / 2, 80, { align: 'center' });

  doc.setFontSize(28);
  doc.setTextColor(20, 20, 20);
  doc.text(lang === 'id' ? 'LAPORAN OSINT' : 'OSINT REPORT', pageW / 2, 180, { align: 'center' });

  doc.setFontSize(11);
  doc.setTextColor(0, 132, 184);
  doc.setFont(undefined, 'normal');
  doc.text('HORUS V2 · Powered by KES', pageW / 2, 210, { align: 'center' });

  doc.setFontSize(14);
  doc.setTextColor(60, 60, 60);
  doc.text(lang === 'id' ? 'Subjek' : 'Subject', pageW / 2, 300, { align: 'center' });
  doc.setFontSize(18);
  doc.setTextColor(20, 20, 20);
  doc.setFont(undefined, 'bold');
  doc.text(subject?.nama_lengkap || subject?.nik || '—', pageW / 2, 326, { align: 'center' });
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text(`NIK: ${subject?.nik || '—'}`, pageW / 2, 348, { align: 'center' });

  // Meta block
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  let coverY = 420;
  doc.setFont(undefined, 'bold'); doc.text(lang === 'id' ? 'Operator:' : 'Operator:', pageW / 2 - 80, coverY, { align: 'right' });
  doc.setFont(undefined, 'normal'); doc.text(operatorId, pageW / 2 - 70, coverY); coverY += 18;
  doc.setFont(undefined, 'bold'); doc.text(lang === 'id' ? 'Dibuat:' : 'Generated:', pageW / 2 - 80, coverY, { align: 'right' });
  doc.setFont(undefined, 'normal'); doc.text(fmtDate(ts, lang), pageW / 2 - 70, coverY); coverY += 18;
  doc.setFont(undefined, 'bold'); doc.text(lang === 'id' ? 'Total Temuan:' : 'Total Findings:', pageW / 2 - 80, coverY, { align: 'right' });
  doc.setFont(undefined, 'normal'); doc.text(String(findings.length), pageW / 2 - 70, coverY); coverY += 18;
  doc.setFont(undefined, 'bold'); doc.text(lang === 'id' ? 'Data Sensitif:' : 'Sensitive Fields:', pageW / 2 - 80, coverY, { align: 'right' });
  doc.setTextColor(192, 0, 0); doc.setFont(undefined, 'bold');
  doc.text(String(elevatedCount), pageW / 2 - 70, coverY);

  // Findings pages
  doc.addPage();
  let y = 60;
  const margin = 40;
  const lineH  = 14;

  for (const [platform, items] of Object.entries(groups)) {
    if (y > pageH - 100) { doc.addPage(); y = 60; }
    doc.setFontSize(14);
    doc.setTextColor(0, 132, 184);
    doc.setFont(undefined, 'bold');
    doc.text(`${platform.toUpperCase()} · ${items.length} ${lang === 'id' ? 'temuan' : 'findings'}`, margin, y);
    y += 22;

    for (const f of items) {
      const r        = f.raw_data || {};
      const conf     = Math.round((f.confidence || 0) * 100);
      const label    = confidenceLabel(f.confidence || 0, lang);
      const heading  = r.publisher || r.authority || r.username || f.source_url || '—';
      const bodyLines = [];
      if (r.headline) bodyLines.push(r.headline);
      if (r.snippet)  bodyLines.push(r.snippet.slice(0, 160));
      if (r.text)     bodyLines.push(r.text.slice(0, 160));
      if (r.key_data) bodyLines.push(r.key_data);
      if (f.signal_explain) bodyLines.push('Match: ' + f.signal_explain);

      // Approximate height needed
      const needed = 30 + bodyLines.length * lineH + (r.is_elevated ? 20 : 0) + (r.phone ? lineH : 0) + (r.post_tagged_location ? lineH : 0);
      if (y + needed > pageH - 60) { doc.addPage(); y = 60; }

      // Heading
      doc.setFontSize(11);
      doc.setTextColor(20, 20, 20);
      doc.setFont(undefined, 'bold');
      doc.text(doc.splitTextToSize(heading, pageW - 2 * margin - 100)[0] || '—', margin, y);

      // Confidence badge (right)
      doc.setFontSize(9);
      doc.setTextColor(conf >= 65 ? 0 : 170, conf >= 65 ? 136 : 102, conf >= 65 ? 68 : 0);
      doc.text(`${label} · ${conf}%`, pageW - margin, y, { align: 'right' });
      doc.setFont(undefined, 'normal');
      y += lineH + 2;

      // ELEVATED badge if applicable
      if (r.is_elevated) {
        doc.setFontSize(8);
        doc.setTextColor(192, 0, 0);
        doc.setFont(undefined, 'bold');
        doc.text('⚠ ' + (lang === 'id' ? 'DATA SENSITIF' : 'SENSITIVE DATA'), margin, y);
        doc.setFont(undefined, 'normal');
        y += lineH;
      }

      // Body lines
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      for (const line of bodyLines) {
        const wrapped = doc.splitTextToSize(line, pageW - 2 * margin);
        for (const w of wrapped.slice(0, 3)) {
          doc.text(w, margin, y);
          y += lineH;
        }
      }

      // Phone (ELEVATED)
      if (r.phone) {
        doc.setFontSize(9);
        doc.setTextColor(192, 0, 0);
        doc.setFont(undefined, 'bold');
        doc.text(`⚠ ${lang === 'id' ? 'Telepon (Sensitif)' : 'Phone (Sensitive)'}: ${r.phone}`, margin, y);
        doc.setFont(undefined, 'normal');
        y += lineH;
      }
      // Post-tagged location (ELEVATED)
      if (r.post_tagged_location) {
        doc.setFontSize(9);
        doc.setTextColor(204, 136, 0);
        doc.setFont(undefined, 'bold');
        doc.text(`⚠ ${lang === 'id' ? 'Lokasi Postingan (Sensitif)' : 'Post Location (Sensitive)'}: ${r.post_tagged_location}`, margin, y);
        doc.setFont(undefined, 'normal');
        y += lineH;
      }

      // URL
      if (f.source_url) {
        doc.setFontSize(8);
        doc.setTextColor(0, 102, 204);
        doc.text(doc.splitTextToSize(f.source_url, pageW - 2 * margin)[0], margin, y);
        y += lineH;
      }

      // Separator
      doc.setDrawColor(220, 220, 220);
      doc.line(margin, y + 2, pageW - margin, y + 2);
      y += 14;
    }
  }

  // Legal footer
  if (y > pageH - 120) { doc.addPage(); y = 60; }
  y += 10;
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.setFont(undefined, 'italic');
  const legal = lang === 'id'
    ? 'Catatan Hukum: Laporan ini berisi data publik yang dikumpulkan via OSINT untuk keperluan investigasi resmi pemerintah. Sesuai UU PDP, data sensitif (telepon, lokasi inferensi) memerlukan justifikasi tambahan untuk diakses ulang. Retensi default 24 bulan. Distribusi terbatas — internal use only.'
    : 'Legal Notice: This report contains public data collected via OSINT for authorized government investigation. Per UU PDP, sensitive fields require additional justification for re-access. Default retention 24 months. Limited distribution — internal use only.';
  const wrapped = doc.splitTextToSize(legal, pageW - 2 * margin);
  for (const w of wrapped) {
    doc.text(w, margin, y);
    y += 12;
  }

  // Apply header/footer to all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    stampPage(i, totalPages);
  }

  // Save
  const filename = `OSINT-Report-${subject?.nik || 'subject'}-${Date.now()}.pdf`;
  doc.save(filename);

  await logReportGeneration(operatorId, subject?.nik, 'pdf', findings.length, elevatedCount);
  return { format: 'pdf', filename, findings_count: findings.length, elevated_count: elevatedCount };
}
